import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, GripVertical, Plus, X, Trash2, Mic, Link as LinkIcon,
  Upload, Loader2, Search, BookOpen, ListOrdered, Tag as TagIcon, User,
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';

// Cloudinary thumb helper
const thumb = (url) => {
  if (!url) return null;
  if (url.includes('cloudinary.com') && url.includes('/upload/')) {
    return url.replace('/upload/', '/upload/t_fitmax-300/');
  }
  return url;
};

// Tour audio bucket — public so the Flutter player can stream w/o auth.
const AUDIO_BUCKET = 'audio';
const TAG_TYPES = ['subject', 'style', 'color', 'feeling']; // align with For You

const CuratorTourEditor = () => {
  const { collectionId: collectionIdParam } = useParams();
  const navigate = useNavigate();
  const collectionId = parseInt(collectionIdParam, 10);

  const [collection, setCollection] = useState(null);
  const [pool, setPool] = useState([]); // [{vc_id, title, artist_name, cover_image, tags:[{type,title}]}]
  const [tour, setTour] = useState([]); // ordered [{vc_id, title, artist_name, cover_image, caption, tour_audio_url}]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  // Filter state
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all'); // all|<tag-type>|artist
  const [filterValue, setFilterValue] = useState(null);

  // Per-stop expansion
  const [expanded, setExpanded] = useState(null);
  const [uploading, setUploading] = useState({});

  // ── Load ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: col, error: colErr } = await supabase
          .from('collections')
          .select('collection_id, title, cover_image')
          .eq('collection_id', collectionId)
          .single();
        if (colErr) throw colErr;

        const { data: rows, error: rowsErr } = await supabase
          .from('collection_vc_mapping')
          .select(
            'vc_id, accepted, tour_position, tour_caption, tour_audio_url, ' +
            'vcs!inner(vc_id, title, cover_image, uuid, users!vcs_uuid_fkey(nickname,first_name,last_name))'
          )
          .eq('collection_id', collectionId)
          .eq('accepted', true);
        if (rowsErr) throw rowsErr;

        const vcIds = (rows || []).map((r) => r.vc_id);

        // Tags for the pool sidebar
        let tagRows = [];
        if (vcIds.length) {
          const { data, error: tErr } = await supabase
            .from('attribute_vc_mapping')
            .select('vc_id, artwork_attributes!inner(id,title,type)')
            .in('vc_id', vcIds);
          if (tErr) throw tErr;
          tagRows = data || [];
        }
        const tagsByVc = {};
        for (const r of tagRows) {
          const a = r.artwork_attributes;
          if (!a) continue;
          (tagsByVc[r.vc_id] ||= []).push({ type: a.type, title: a.title });
        }

        const enriched = (rows || []).map((r) => {
          const u = r.vcs?.users;
          const artistName =
            u?.nickname ||
            [u?.first_name, u?.last_name].filter(Boolean).join(' ') ||
            'Unknown artist';
          return {
            vc_id: r.vc_id,
            title: r.vcs?.title || 'Untitled',
            artist_name: artistName,
            cover_image: r.vcs?.cover_image,
            tags: tagsByVc[r.vc_id] || [],
            tour_position: r.tour_position,
            tour_caption: r.tour_caption || '',
            tour_audio_url: r.tour_audio_url || '',
          };
        });

        if (cancelled) return;
        setCollection(col);

        const inTour = enriched
          .filter((a) => Number.isFinite(a.tour_position))
          .sort((a, b) => a.tour_position - b.tour_position);
        const notInTour = enriched.filter((a) => !Number.isFinite(a.tour_position));

        setTour(
          inTour.map((a) => ({
            vc_id: a.vc_id,
            title: a.title,
            artist_name: a.artist_name,
            cover_image: a.cover_image,
            caption: a.tour_caption,
            tour_audio_url: a.tour_audio_url,
          }))
        );
        setPool(enriched);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [collectionId]);

  // ── Derived ───────────────────────────────────────────────────────
  const tourIds = useMemo(() => new Set(tour.map((t) => t.vc_id)), [tour]);

  const tagFacets = useMemo(() => {
    const counts = {}; // `${type}::${title}` -> count
    for (const a of pool) {
      for (const t of a.tags) {
        if (!TAG_TYPES.includes(t.type)) continue;
        const key = `${t.type}::${t.title}`;
        counts[key] = (counts[key] || 0) + 1;
      }
    }
    const grouped = {};
    for (const k of Object.keys(counts)) {
      const [type, title] = k.split('::');
      (grouped[type] ||= []).push({ title, count: counts[k] });
    }
    for (const t of Object.keys(grouped)) {
      grouped[t].sort((a, b) => b.count - a.count);
    }
    return grouped;
  }, [pool]);

  const artistFacets = useMemo(() => {
    const counts = {};
    for (const a of pool) {
      counts[a.artist_name] = (counts[a.artist_name] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [pool]);

  const filteredPool = useMemo(() => {
    let out = pool;
    if (filterType !== 'all' && filterValue) {
      if (filterType === 'artist') {
        out = out.filter((a) => a.artist_name === filterValue);
      } else {
        out = out.filter((a) =>
          a.tags.some((t) => t.type === filterType && t.title === filterValue)
        );
      }
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.artist_name.toLowerCase().includes(q)
      );
    }
    return out;
  }, [pool, filterType, filterValue, search]);

  // ── Mutations ─────────────────────────────────────────────────────
  const addToTour = useCallback(
    (artwork) => {
      if (tourIds.has(artwork.vc_id)) return;
      setTour((prev) => [
        ...prev,
        {
          vc_id: artwork.vc_id,
          title: artwork.title,
          artist_name: artwork.artist_name,
          cover_image: artwork.cover_image,
          caption: '',
          tour_audio_url: '',
        },
      ]);
    },
    [tourIds]
  );

  const removeFromTour = useCallback((vcId) => {
    setTour((prev) => prev.filter((t) => t.vc_id !== vcId));
    setExpanded((e) => (e === vcId ? null : e));
  }, []);

  const moveStop = useCallback((from, to) => {
    setTour((prev) => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  const updateStop = useCallback((vcId, patch) => {
    setTour((prev) =>
      prev.map((t) => (t.vc_id === vcId ? { ...t, ...patch } : t))
    );
  }, []);

  const autoNumberFromCurrentOrder = useCallback(() => {
    if (!confirm('Replace the current tour with all artworks in their current order?')) return;
    setTour(
      pool.map((a) => ({
        vc_id: a.vc_id,
        title: a.title,
        artist_name: a.artist_name,
        cover_image: a.cover_image,
        caption: a.tour_caption || '',
        tour_audio_url: a.tour_audio_url || '',
      }))
    );
  }, [pool]);

  const clearTour = useCallback(() => {
    if (!tour.length) return;
    if (!confirm('Remove all stops from the tour? (Click Save to persist.)')) return;
    setTour([]);
    setExpanded(null);
  }, [tour.length]);

  const handleAudioUpload = useCallback(
    async (vcId, file) => {
      if (!file) return;
      setUploading((u) => ({ ...u, [vcId]: true }));
      try {
        const ext = file.name.split('.').pop() || 'mp3';
        const path = `tour/${collectionId}/${vcId}-${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from(AUDIO_BUCKET)
          .upload(path, file, { upsert: true, contentType: file.type });
        if (upErr) throw upErr;
        const { data: pub } = supabase.storage.from(AUDIO_BUCKET).getPublicUrl(path);
        updateStop(vcId, { tour_audio_url: pub.publicUrl });
      } catch (e) {
        alert(`Upload failed: ${e.message}`);
      } finally {
        setUploading((u) => ({ ...u, [vcId]: false }));
      }
    },
    [collectionId, updateStop]
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      // 1) Clear tour fields on every mapping for this collection (start clean)
      const { error: clearErr } = await supabase
        .from('collection_vc_mapping')
        .update({
          tour_position: null,
          tour_caption: null,
          tour_audio_url: null,
        })
        .eq('collection_id', collectionId);
      if (clearErr) throw clearErr;

      // 2) Apply each stop with its position. Sequential so Postgres sees
      //    discrete updates (tour is small — 10s of rows, not 1000s).
      for (let i = 0; i < tour.length; i += 1) {
        const stop = tour[i];
        const { error: upErr } = await supabase
          .from('collection_vc_mapping')
          .update({
            tour_position: i + 1,
            tour_caption: stop.caption || null,
            tour_audio_url: stop.tour_audio_url || null,
          })
          .eq('collection_id', collectionId)
          .eq('vc_id', stop.vc_id);
        if (upErr) throw upErr;
      }
      setSavedAt(new Date());
    } catch (e) {
      setError(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  }, [collectionId, tour]);

  // ── Drag & drop reorder (HTML5) ───────────────────────────────────
  const dragIndexRef = React.useRef(null);
  const onDragStart = (i) => () => {
    dragIndexRef.current = i;
  };
  const onDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from == null || from === i) return;
    moveStop(from, i);
    dragIndexRef.current = null;
  };

  // ── Render ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={S.center}>
        <Loader2 size={28} className="spin" /> <span style={{ marginLeft: 8 }}>Loading tour…</span>
      </div>
    );
  }
  if (error && !pool.length) {
    return (
      <div style={S.center}>
        <p style={{ color: '#b91c1c' }}>{error}</p>
        <button onClick={() => navigate(-1)} style={S.btn}>Back</button>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <button onClick={() => navigate(-1)} style={S.iconBtn} title="Back">
          <ArrowLeft size={20} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={S.crumb}>
            <BookOpen size={14} /> {collection?.title}
          </div>
          <h1 style={S.title}>Edit tour</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {savedAt && (
            <span style={{ fontSize: 12, color: '#16a34a' }}>
              Saved {savedAt.toLocaleTimeString()}
            </span>
          )}
          {error && <span style={{ fontSize: 12, color: '#b91c1c' }}>{error}</span>}
          <button onClick={clearTour} style={S.btnGhost} disabled={!tour.length}>
            <Trash2 size={14} /> Clear
          </button>
          <button onClick={autoNumberFromCurrentOrder} style={S.btnGhost}>
            <ListOrdered size={14} /> Auto-fill from order
          </button>
          <button onClick={handleSave} style={S.btnPrimary} disabled={saving}>
            {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
            Save tour
          </button>
        </div>
      </div>

      <div style={S.body}>
        {/* LEFT: filters */}
        <aside style={S.left}>
          <button
            style={{
              ...S.facet,
              ...(filterType === 'all' ? S.facetActive : null),
            }}
            onClick={() => {
              setFilterType('all');
              setFilterValue(null);
            }}
          >
            All artworks <span style={S.facetCount}>{pool.length}</span>
          </button>

          {TAG_TYPES.map((type) => {
            const list = tagFacets[type] || [];
            if (!list.length) return null;
            return (
              <div key={type} style={S.facetGroup}>
                <div style={S.facetHeader}>
                  <TagIcon size={12} /> {type}
                </div>
                {list.slice(0, 30).map((t) => {
                  const active = filterType === type && filterValue === t.title;
                  return (
                    <button
                      key={t.title}
                      style={{ ...S.facet, ...(active ? S.facetActive : null) }}
                      onClick={() => {
                        setFilterType(type);
                        setFilterValue(t.title);
                      }}
                    >
                      {t.title} <span style={S.facetCount}>{t.count}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}

          {artistFacets.length > 0 && (
            <div style={S.facetGroup}>
              <div style={S.facetHeader}>
                <User size={12} /> artist
              </div>
              {artistFacets.slice(0, 30).map((a) => {
                const active = filterType === 'artist' && filterValue === a.name;
                return (
                  <button
                    key={a.name}
                    style={{ ...S.facet, ...(active ? S.facetActive : null) }}
                    onClick={() => {
                      setFilterType('artist');
                      setFilterValue(a.name);
                    }}
                  >
                    {a.name} <span style={S.facetCount}>{a.count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </aside>

        {/* MIDDLE: pool */}
        <main style={S.middle}>
          <div style={S.searchRow}>
            <Search size={16} color="#6b7280" />
            <input
              placeholder="Search artwork or artist…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={S.searchInput}
            />
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              {filteredPool.length} shown
            </span>
          </div>
          <div style={S.grid}>
            {filteredPool.map((a) => {
              const inTour = tourIds.has(a.vc_id);
              const position = inTour
                ? tour.findIndex((t) => t.vc_id === a.vc_id) + 1
                : null;
              return (
                <button
                  key={a.vc_id}
                  onClick={() => (inTour ? removeFromTour(a.vc_id) : addToTour(a))}
                  style={{
                    ...S.card,
                    ...(inTour ? S.cardActive : null),
                  }}
                  title={inTour ? 'Click to remove from tour' : 'Click to add to tour'}
                >
                  <div style={S.cardImgWrap}>
                    {a.cover_image ? (
                      <img src={thumb(a.cover_image)} alt={a.title} style={S.cardImg} />
                    ) : (
                      <div style={{ ...S.cardImg, background: '#f3f4f6' }} />
                    )}
                    {inTour && <div style={S.cardBadge}>{position}</div>}
                    {!inTour && (
                      <div style={S.cardAdd}>
                        <Plus size={16} />
                      </div>
                    )}
                  </div>
                  <div style={S.cardTitle}>{a.title}</div>
                  <div style={S.cardArtist}>by {a.artist_name}</div>
                </button>
              );
            })}
            {!filteredPool.length && (
              <div style={{ color: '#6b7280', padding: 24 }}>No artworks match.</div>
            )}
          </div>
        </main>

        {/* RIGHT: tour */}
        <aside style={S.right}>
          <div style={S.rightHeader}>
            The tour <span style={{ color: '#6b7280', fontWeight: 400 }}>· {tour.length} stop{tour.length === 1 ? '' : 's'}</span>
          </div>
          {!tour.length && (
            <div style={S.empty}>
              <Mic size={28} color="#9ca3af" />
              <p style={{ color: '#6b7280', marginTop: 8 }}>
                Pick artworks from the middle to start your tour. Drag to reorder.
              </p>
            </div>
          )}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {tour.map((stop, i) => {
              const isOpen = expanded === stop.vc_id;
              return (
                <div
                  key={stop.vc_id}
                  draggable
                  onDragStart={onDragStart(i)}
                  onDragOver={onDragOver(i)}
                  onDrop={onDrop(i)}
                  style={S.stop}
                >
                  <div style={S.stopRow}>
                    <span style={{ cursor: 'grab', color: '#9ca3af' }}>
                      <GripVertical size={16} />
                    </span>
                    <span style={S.stopNum}>{i + 1}</span>
                    {stop.cover_image ? (
                      <img src={thumb(stop.cover_image)} alt="" style={S.stopThumb} />
                    ) : (
                      <div style={{ ...S.stopThumb, background: '#f3f4f6' }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={S.stopTitle}>{stop.title}</div>
                      <div style={S.stopArtist}>by {stop.artist_name}</div>
                    </div>
                    <button
                      style={S.iconBtnSm}
                      onClick={() => setExpanded(isOpen ? null : stop.vc_id)}
                      title="Edit caption + audio"
                    >
                      <Mic size={14} />
                    </button>
                    <button
                      style={S.iconBtnSm}
                      onClick={() => removeFromTour(stop.vc_id)}
                      title="Remove"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {isOpen && (
                    <div style={S.stopEditor}>
                      <label style={S.lbl}>Caption</label>
                      <textarea
                        value={stop.caption}
                        onChange={(e) => updateStop(stop.vc_id, { caption: e.target.value })}
                        placeholder="A short note shown over the artwork…"
                        style={S.textarea}
                        rows={3}
                      />
                      <label style={S.lbl}>Audio narration</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          type="text"
                          value={stop.tour_audio_url}
                          onChange={(e) =>
                            updateStop(stop.vc_id, { tour_audio_url: e.target.value })
                          }
                          placeholder="https://… or upload an MP3"
                          style={{ ...S.textarea, height: 32, padding: '4px 8px' }}
                        />
                        <label style={S.btnGhost}>
                          {uploading[stop.vc_id] ? (
                            <Loader2 size={14} className="spin" />
                          ) : (
                            <Upload size={14} />
                          )}
                          Upload
                          <input
                            type="file"
                            accept="audio/*"
                            style={{ display: 'none' }}
                            onChange={(e) =>
                              handleAudioUpload(stop.vc_id, e.target.files?.[0])
                            }
                          />
                        </label>
                      </div>
                      {stop.tour_audio_url && (
                        <audio
                          controls
                          src={stop.tour_audio_url}
                          style={{ width: '100%', marginTop: 8 }}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
};

const S = {
  page: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f9fafb',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 20px',
    background: '#fff',
    borderBottom: '1px solid #e5e7eb',
  },
  crumb: {
    fontSize: 12,
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  title: { fontSize: 20, fontWeight: 700, margin: 0 },
  body: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '240px 1fr 360px',
    minHeight: 0,
  },
  left: {
    background: '#fff',
    borderRight: '1px solid #e5e7eb',
    padding: 12,
    overflowY: 'auto',
  },
  middle: {
    overflowY: 'auto',
    padding: 12,
    minWidth: 0,
  },
  right: {
    background: '#fff',
    borderLeft: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  rightHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid #e5e7eb',
    fontWeight: 600,
    fontSize: 14,
  },
  empty: { padding: 32, textAlign: 'center' },
  facetGroup: { marginTop: 12 },
  facetHeader: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: '#6b7280',
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  facet: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 8px',
    border: 'none',
    background: 'transparent',
    color: '#374151',
    fontSize: 13,
    cursor: 'pointer',
    textAlign: 'left',
    borderRadius: 4,
  },
  facetActive: { background: '#eef2ff', color: '#4338ca', fontWeight: 600 },
  facetCount: { fontSize: 11, color: '#9ca3af', fontVariantNumeric: 'tabular-nums' },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 8,
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    marginBottom: 12,
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: 14,
    background: 'transparent',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))',
    gap: 12,
  },
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    padding: 6,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  cardActive: { borderColor: '#4338ca', borderWidth: 2, padding: 5 },
  cardImgWrap: { position: 'relative', aspectRatio: '1 / 1', overflow: 'hidden', background: '#f3f4f6', borderRadius: 4 },
  cardImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  cardBadge: {
    position: 'absolute',
    top: 4,
    left: 4,
    background: '#4338ca',
    color: '#fff',
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
  },
  cardAdd: {
    position: 'absolute',
    top: 4,
    right: 4,
    background: 'rgba(0,0,0,0.5)',
    color: '#fff',
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardTitle: { fontSize: 12, fontWeight: 600, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardArtist: { fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  stop: { borderBottom: '1px solid #f3f4f6' },
  stopRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px' },
  stopNum: {
    background: '#eef2ff',
    color: '#4338ca',
    fontWeight: 700,
    fontSize: 12,
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontVariantNumeric: 'tabular-nums',
  },
  stopThumb: { width: 36, height: 36, objectFit: 'cover', borderRadius: 4 },
  stopTitle: { fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  stopArtist: { fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  stopEditor: { padding: '0 12px 12px 42px' },
  lbl: { fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', display: 'block', marginTop: 8, marginBottom: 4 },
  textarea: {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: 4,
    padding: 6,
    fontSize: 13,
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  iconBtn: { background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, color: '#374151' },
  iconBtnSm: { background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'pointer', padding: 4, color: '#6b7280' },
  btn: { padding: '6px 12px', borderRadius: 4, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 },
  btnGhost: { padding: '6px 10px', borderRadius: 4, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 },
  btnPrimary: { padding: '6px 14px', borderRadius: 4, border: 'none', background: '#4338ca', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 8 },
};

export default CuratorTourEditor;
